import React, { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

export const CameraSetup = ({
    cameraFOV,
    cameraNear,
    cameraFar,
    cameraPositionX,
    cameraPositionY,
    cameraPositionZ,
    cameraRotationX,
    cameraRotationY,
    cameraRotationZ
}) => {
    const { scene, camera, size } = useThree();
    const isInitialized = useRef(false);
    const lastPosition = useRef({ x: cameraPositionX, y: cameraPositionY, z: cameraPositionZ });
    const lastRotation = useRef({ x: cameraRotationX, y: cameraRotationY, z: cameraRotationZ });

    // Setze Kamera-Parameter immer (FOV, near, far, aspect)
    useEffect(() => {
        camera.fov = cameraFOV;
        camera.near = cameraNear;
        camera.far = cameraFar;
        
        // WICHTIG: Stelle sicher, dass die Aspect-Ratio korrekt ist
        // React Three Fiber setzt size.width/height automatisch
        camera.aspect = size.width / size.height;
        camera.updateProjectionMatrix();
    }, [camera, cameraFOV, cameraNear, cameraFar, size.width, size.height]);

    // Setze Position und Rotation NUR beim ersten Mount
    // Danach wird die Kamera komplett von OrbitControls gesteuert
    // Position/Rotation werden NUR gesetzt wenn sich die Props WESENTLICH ändern (durch UI)
    useEffect(() => {
        if (!isInitialized.current) {
            // Initialisiere Kamera nur beim ersten Mount
            camera.position.set(cameraPositionX, cameraPositionY, cameraPositionZ);
            camera.rotation.set(cameraRotationX, cameraRotationY, cameraRotationZ);
            lastPosition.current = { x: cameraPositionX, y: cameraPositionY, z: cameraPositionZ };
            lastRotation.current = { x: cameraRotationX, y: cameraRotationY, z: cameraRotationZ };
            isInitialized.current = true;
            return;
        }

        // Nach der Initialisierung: Nur setzen wenn sich Props WESENTLICH ändern
        // (z.B. durch UI-Eingaben, nicht durch OrbitControls)
        const positionChanged = 
            Math.abs(lastPosition.current.x - cameraPositionX) > 0.5 ||
            Math.abs(lastPosition.current.y - cameraPositionY) > 0.5 ||
            Math.abs(lastPosition.current.z - cameraPositionZ) > 0.5;
        
        const rotationChanged = 
            Math.abs(lastRotation.current.x - cameraRotationX) > 0.1 ||
            Math.abs(lastRotation.current.y - cameraRotationY) > 0.1 ||
            Math.abs(lastRotation.current.z - cameraRotationZ) > 0.1;

        // Prüfe ob die Kamera aktuell weit von den Props entfernt ist
        // Wenn ja, wurde sie wahrscheinlich durch OrbitControls bewegt
        const currentPosDiff = 
            Math.abs(camera.position.x - cameraPositionX) +
            Math.abs(camera.position.y - cameraPositionY) +
            Math.abs(camera.position.z - cameraPositionZ);
        
        // Nur setzen wenn sich Props geändert haben UND Kamera nicht durch User bewegt wurde
        if (positionChanged && currentPosDiff < 1.0) {
            camera.position.set(cameraPositionX, cameraPositionY, cameraPositionZ);
            lastPosition.current = { x: cameraPositionX, y: cameraPositionY, z: cameraPositionZ };
        }
        
        if (rotationChanged && currentPosDiff < 1.0) {
            camera.rotation.set(cameraRotationX, cameraRotationY, cameraRotationZ);
            lastRotation.current = { x: cameraRotationX, y: cameraRotationY, z: cameraRotationZ };
        }
    }, [camera, cameraPositionX, cameraPositionY, cameraPositionZ, cameraRotationX, cameraRotationY, cameraRotationZ]);

    return null;
};
